/* De tekst van de offerte: projectdoel, werkomschrijving, advies en disclaimer. Elk
 * veld bewaart zichzelf (na 0,8 s stilte en bij wegklikken); de PDF rechts ververst mee.
 * De pagina geeft dit blok een nieuwe key bij een andere offerte of als een sjabloon
 * de teksten vervangt; dan begint het opnieuw met wat er in de offerte staat. */

import { useCallback, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import type { Quote } from '@/types/quote';
import { RichTextEditor } from '@/components/crm/quote/RichTextEditor';

type Veld = 'project_goal' | 'work_description' | 'advice_conclusion' | 'disclaimer';

const VELDEN: { id: Veld; titel: string; uitleg: string; hoogte: string; placeholder: string }[] = [
  {
    id: 'project_goal',
    titel: 'Projectdoel',
    uitleg: 'Achtergrond en wat de klant wil bereiken.',
    hoogte: '120px',
    placeholder: 'Wat is de achtergrond, wat wil de klant bereiken, welk probleem lossen we op?',
  },
  {
    id: 'work_description',
    titel: 'Werkomschrijving',
    uitleg: 'De werkzaamheden die we uitvoeren.',
    hoogte: '220px',
    placeholder: 'Opstelling, aansluiting, functionaliteit, overdracht…',
  },
  {
    id: 'advice_conclusion',
    titel: 'Advies en conclusie',
    uitleg: 'Ons advies en de afsluiting.',
    hoogte: '120px',
    placeholder: 'Op basis van bovenstaande configuratie adviseren wij…',
  },
  {
    id: 'disclaimer',
    titel: 'Disclaimer',
    uitleg: 'Voorbehouden en juridische toelichting.',
    hoogte: '90px',
    placeholder: 'Deze offerte is uitgebracht op basis van de bij ons bekende gegevens…',
  },
];

type Bewaar = (w: Partial<Quote>, o?: { silent?: boolean; refetch?: boolean }) => Promise<void>;

export function TekstBlok({ quote, onUpdate }: { quote: Quote; onUpdate: Bewaar }) {
  const [waarden, setWaarden] = useState<Record<Veld, string>>(() => ({
    project_goal: quote.project_goal ?? '',
    work_description: quote.work_description ?? '',
    advice_conclusion: quote.advice_conclusion ?? '',
    disclaimer: quote.disclaimer ?? '',
  }));
  const [bezig, setBezig] = useState<Partial<Record<Veld, boolean>>>({});
  const wachten = useRef<Partial<Record<Veld, ReturnType<typeof setTimeout>>>>({});
  const bewaard = useRef<Record<Veld, string>>({ ...waarden });

  const bewaar = useCallback(async (veld: Veld, waarde: string) => {
    if (bewaard.current[veld] === waarde) return;
    setBezig((b) => ({ ...b, [veld]: true }));
    try {
      await onUpdate({ [veld]: waarde || null } as Partial<Quote>, { silent: true, refetch: false });
      bewaard.current[veld] = waarde;
    } finally {
      setBezig((b) => ({ ...b, [veld]: false }));
    }
  }, [onUpdate]);

  const wijzig = (veld: Veld, waarde: string) => {
    setWaarden((w) => ({ ...w, [veld]: waarde }));
    clearTimeout(wachten.current[veld]);
    wachten.current[veld] = setTimeout(() => bewaar(veld, waarde), 800);
  };

  return (
    <div className="flex flex-col divide-y rounded-xl border bg-background">
      {VELDEN.map((v) => (
        <div key={v.id} className="flex flex-col gap-2 px-4 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <div className="flex min-w-0 items-baseline gap-2">
              <h3 className="text-sm font-semibold">{v.titel}</h3>
              <span className="hidden truncate text-xs text-muted-foreground sm:inline">{v.uitleg}</span>
            </div>
            <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              {bezig[v.id] ? (
                <><Loader2 className="h-3 w-3 animate-spin" />Opslaan…</>
              ) : waarden[v.id] ? (
                <><Check className="h-3 w-3 text-green-600" />bewaard</>
              ) : (
                'leeg'
              )}
            </span>
          </div>
          <RichTextEditor
            value={waarden[v.id]}
            onChange={(html) => wijzig(v.id, html)}
            onBlur={() => {
              clearTimeout(wachten.current[v.id]);
              bewaar(v.id, waarden[v.id]);
            }}
            placeholder={v.placeholder}
            minHeight={v.hoogte}
          />
        </div>
      ))}
    </div>
  );
}
